// Mi espacio · Hoy — what the simplified screen shows, computed from what is already loaded (pure, tested by
// scripts/test-today.mjs). Nothing here reads, writes or stores anything: it only arranges the records the
// screen already has (the day of owner-space, «Por atender» of coaching-admin, the commercial queue of
// src/followupQueue.js) so that every pending item appears once, with its original record and state.

/** Commercial follow-ups that are due today (the queue's own tags); «re-engage» stays in the full queue. */
export const DUE_FOLLOWUP_TAGS = ['urgent', 'followup'];

/** The sections of «Pendientes», in order. */
export const PENDING_SECTIONS = [
  { key: 'overdue', label: 'Vencidas' },
  { key: 'today', label: 'Para hoy' },
  { key: 'coaching', label: 'Acompañamiento' },
  { key: 'commercial', label: 'Comercial' },
];

const ms = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? t : null; };

/**
 * The next appointment of the day: the one going on now, else the next one to start. A Calendar event marked
 * «libre» is not an appointment (it stays in «Ver el día»). Another day than today: its first appointment.
 * `remaining`: how many appointments are still ahead (the one shown included); `total`: all of the day.
 */
export function nextAppointment(agenda = [], { date, today, now = Date.now() } = {}) {
  const items = agenda.filter(a => ms(a.start) !== null && (a.source === 'oike' || a.busy !== false))
    .sort((a, b) => ms(a.start) - ms(b.start));
  if (date !== today) return { item: items[0] || null, ongoing: false, remaining: items.length, total: items.length };
  const ongoing = items.find(a => ms(a.start) <= now && now < (ms(a.end) ?? ms(a.start)));
  const ahead = items.filter(a => ms(a.start) > now);
  const item = ongoing || ahead[0] || null;
  return { item, ongoing: !!ongoing, remaining: ahead.length + (ongoing ? 1 : 0), total: items.length };
}

const recordKey = (type, id) => `${type}:${id}`;

/** The open next actions of each contact (Personal Tasks with Source kind contact), by stakeholder id, earliest first. */
export function contactTasksBy(day) {
  const all = [...(day?.overdue || []).filter(t => t.type === 'task' || !t.type), ...(day?.tasks || []), ...(day?.upcoming || []), ...(day?.undated || [])];
  const map = new Map();
  const seen = new Set();
  for (const t of all) {
    if (t?.status !== 'open' || t.source?.kind !== 'contact' || !t.source.stakeholderId || seen.has(t.id)) continue;
    seen.add(t.id);
    if (!map.has(t.source.stakeholderId)) map.set(t.source.stakeholderId, []);
    map.get(t.source.stakeholderId).push(t);
  }
  for (const list of map.values()) list.sort((a, b) => String(a.dueOn || '9999').localeCompare(String(b.dueOn || '9999')));
  return map;
}

/**
 * Every pending item once, in sections. The key is the record (task, goal action, coaching session, message,
 * submission, commercial contact), so an item that comes from several places appears once (the first place wins).
 * A commercial contact is ONE row in «Comercial»: its next action due today or overdue, its answer to give (with the
 * next action still visible when it is planned for later) or, only when nothing is planned for it, the automatic
 * follow-up. An item that is also one of the day's saved priorities is marked, not repeated.
 *   day: owner-space's day; attention: coaching-admin › attention (null when not loaded or not the coach);
 *   commercial: { replies, followups } of todayPriorities.plainFollowups (null without commercial data);
 *   contacts: optional Map stakeholderId → { name, accountName, accountId } to name a planned contact.
 */
export function pendingItems({ day, attention = null, commercial = null, contacts = null }) {
  const prio = new Set((day?.priorities || []).filter(p => p.exists !== false && p.id && p.kind !== 'text').map(p => recordKey(p.kind, p.id)));
  const out = [];
  const seen = new Set();
  const add = (section, type, kind, id, record, extra = {}) => {
    const key = recordKey(kind, id);
    if (!id || seen.has(key)) return;
    seen.add(key);
    out.push({ key, section, type, id, record, priority: prio.has(key) || (extra.planned || []).some(t => prio.has(recordKey('task', t.id))), ...extra });
  };
  const planned = contactTasksBy(day);
  const today = day?.date || '';
  const isContactTask = (t) => t?.source?.kind === 'contact' && t.source.stakeholderId;
  for (const o of day?.overdue || []) if (!isContactTask(o)) add('overdue', o.type === 'task' ? 'task' : 'application', o.type === 'task' ? 'task' : 'application', o.id, o);
  for (const t of (day?.tasks || []).filter(t => t.status === 'open' && !isContactTask(t))) add('today', 'task', 'task', t.id, t);
  for (const a of (day?.actions || []).filter(a => a.status !== 'done')) add('today', 'application', 'application', a.id, a);
  if (attention) {
    for (const s of attention.toSchedule || []) add('coaching', 'booking', 'session', s.sessionId, s);
    for (const m of attention.messages || []) add('coaching', 'message', 'notification', m.id, m);
    for (const r of attention.reviews || []) add('coaching', 'review', 'submission', r.submissionId, r);
  }
  const replyOf = new Map((commercial?.replies || []).map(r => [r.id, r]));
  const dueOf = (sid) => (planned.get(sid) || []).filter(t => t.dueOn && t.dueOn <= today);
  const info = (sid) => contacts?.get?.(sid) || {};
  // 1. Next actions due today or overdue (with the answer, if they also wrote)
  for (const [sid, tasks] of planned) {
    const due = dueOf(sid);
    if (!due.length) continue;
    const reply = replyOf.get(sid);
    add('commercial', reply ? 'reply' : 'planned', 'contact', sid, reply || { id: sid, ...info(sid) }, { planned: tasks, overdue: due.some(t => t.dueOn < today) });
  }
  // 2. Answers to give (their next action, if planned for later, stays visible)
  for (const r of commercial?.replies || []) add('commercial', 'reply', 'contact', r.id, r, { planned: planned.get(r.id) || [] });
  // 3. Automatic follow-ups, only for contacts with nothing planned
  for (const f of (commercial?.followups || []).filter(f => DUE_FOLLOWUP_TAGS.includes(f.tag))) {
    if (planned.has(f.id)) continue;
    add('commercial', 'followup', 'contact', f.id, f, { planned: [] });
  }
  return out;
}

/** How many items each section has (pure). */
export const sectionCounts = (items) => Object.fromEntries(PENDING_SECTIONS.map(s => [s.key, items.filter(i => i.section === s.key).length]));
